import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { PublicProfileRenderOpenRole } from '@tabbio/types';
import { colors, lightTheme } from '@tabbio/tokens';
import { buildAppJobUrl, buildCanonicalJobUrl } from '@tabbio/utils';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { useToast } from '../../hooks/useToast';
import { useUpgradeModal } from '../../providers/UpgradeModalProvider';
import { WebPageHeader } from '../common/WebPageHeader';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import {
  getEditableProfileBlocks,
  PUBLIC_PROFILE_BLOCK_META,
  PUBLIC_PROFILE_CONTACT_ROW_ORDER,
  PUBLIC_PROFILE_SOCIAL_ORDER_COMPANY,
  PUBLIC_PROFILE_TOP_SOCIALS,
  type PublicProfileBlockType,
  type PublicProfileContactChannelId,
  type PublicProfileSocialId,
} from '../../lib/public-profile-builder';
import {
  getPublicProfileRoutes,
  type PublicProfileRoutePath,
} from '../../lib/public-profile-routes';
import { buildPublicProfileUrl } from '../../lib/public-web';
import {
  getProfileBrand,
  ProfileBrandIcon,
  type ProfileIconId,
} from './ProfileIconRegistry';
import { ProfileBottomBar } from './ProfileBottomBar';
import { CompanyPageBottomBar } from './CompanyPageBottomBar';
import { CareersBlockInline } from './CareersBlockInline.web';
import { useCompanyOpenRoles } from './useCompanyOpenRoles';
import { DocumentDetailSkeleton } from '../common/DocumentDetailSkeleton.web';
import { WebShareDialog } from '../common/WebShareDialog.web';
import {
  createPublicProfilePreviewModel,
  type PublicProfilePreviewCompanyRole,
} from './PublicProfilePreviewModel';
import { PublicProfilePreview } from './PublicProfilePreview';
import { useLocale } from '../../hooks/useLocale';

const LOGO_MAIN = require('../../../assets/branding/logo-main.png');

function mapSocialIcon(id: PublicProfileSocialId): ProfileIconId {
  if (id === 'tabbio') return 'tabbio';
  if (id === 'email') return 'email';
  if (id === 'whatsapp') return 'whatsapp';
  return id as ProfileIconId;
}

function mapContactIcon(id: PublicProfileContactChannelId): ProfileIconId {
  if (id === 'tabbioMessage') return 'tabbio';
  if (id === 'whatsapp') return 'whatsapp';
  if (id === 'telegram') return 'telegram';
  if (id === 'slack') return 'slack';
  if (id === 'email') return 'email';
  if (id === 'landline') return 'landline';
  return 'phone';
}

const MATERIAL_ICON_BY_BLOCK_TYPE: Record<PublicProfileBlockType, string> = {
  webcv: 'description',
  career: 'work',
  services: 'storefront',
  website: 'language',
  insights: 'insights',
  faqs: 'help',
  links: 'link',
  visit: 'place',
  openroles: 'work',
  team: 'groups',
  talentpool: 'groups_3',
  portfolio: 'grid_view',
  testimonial: 'reviews',
  booking: 'event',
  map: 'map',
  pdf: 'picture_as_pdf',
  image: 'image',
  video: 'play_circle',
  digitalproduct: 'inventory_2',
  text: 'subject',
  header: 'title',
  form: 'description',
  music: 'music_note',
};

function BubbleButton({
  onClick,
  children,
  size = 44,
  bg,
  color,
  borderColor,
  ariaLabel,
}: {
  onClick?: () => void;
  children: React.ReactNode;
  size?: number;
  bg: string;
  color?: string;
  borderColor?: string;
  ariaLabel?: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bg,
        color,
        border: borderColor ? `1px solid ${borderColor}` : 'none',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function BrandBubble({
  id,
  onClick,
  size = 42,
}: {
  id: ProfileIconId;
  onClick?: () => void;
  size?: number;
}) {
  const brand = getProfileBrand(id);
  return (
    <BubbleButton
      onClick={onClick}
      size={size}
      bg={brand.bg}
      borderColor={brand.border}
      ariaLabel={id}
    >
      <ProfileBrandIcon
        id={id}
        size={id === 'tabbio' ? 17 : 18}
        color={brand.text}
      />
    </BubbleButton>
  );
}

function BlockRow({
  blockId,
  iconName,
  title,
  subtitle,
  enabled,
  isRTL,
  isDragging,
  isDragOver,
  onOpenEditor,
  onToggle,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onDragEnter,
}: {
  blockId: string;
  iconName: string;
  title: string;
  subtitle: string;
  enabled: boolean;
  isRTL: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onOpenEditor: () => void;
  onToggle: () => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnter: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  return (
    <div
      data-profile-block-card
      data-block-id={blockId}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnter={onDragEnter}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        minHeight: 68,
        borderRadius: 20,
        backgroundColor: hover ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
        outline: isDragOver ? '2px solid var(--text-secondary)' : 'none',
        outlineOffset: -2,
        opacity: isDragging ? 0.45 : isDragOver ? 0.95 : enabled ? 1 : 0.7,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        transition: 'opacity 120ms ease, background-color 140ms',
      }}
    >
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={(event) => event.stopPropagation()}
        aria-hidden
        style={{
          width: 22,
          height: 40,
          display: 'grid',
          placeItems: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          color: 'var(--text-tertiary)',
          opacity: hover ? 1 : 0.55,
          transition: 'opacity 140ms',
          flexShrink: 0,
        }}
      >
        <MaterialSymbol
          name="drag_indicator"
          size={20}
          style={{ color: 'var(--text-tertiary)' }}
        />
      </div>
      <button
        onClick={onOpenEditor}
        style={{
          border: 'none',
          background: 'transparent',
          padding: 0,
          margin: 0,
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: 'pointer',
          textAlign: 'start',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: 'rgba(0,0,0,0.06)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--text-primary)',
            flexShrink: 0,
          }}
        >
          <MaterialSymbol name={iconName} size={18} />
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <div
            style={{
              fontSize: 15,
              fontWeight: 500,
              color: 'var(--text-primary)',
              lineHeight: '20px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
              lineHeight: '17px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </div>
        </div>
      </button>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <MaterialSymbol
          name="chevron_right"
          size={18}
          style={{
            color: 'var(--text-tertiary)',
            opacity: hover ? 1 : 0,
            transition: 'opacity 140ms',
          }}
        />
        <Toggle checked={enabled} onToggle={onToggle} isRTL={isRTL} />
      </div>
    </div>
  );
}

function Toggle({
  checked,
  onToggle,
  isRTL,
}: {
  checked: boolean;
  onToggle: () => void;
  isRTL: boolean;
}) {
  return (
    <button
      onClick={onToggle}
      role="switch"
      aria-checked={checked}
      style={{
        width: 44,
        height: 26,
        borderRadius: 999,
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        padding: 0,
        backgroundColor: checked ? '#22C55E' : 'rgba(0,0,0,0.06)',
        transition: 'background-color 150ms ease',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          insetInlineStart: 2,
          width: 22,
          height: 22,
          borderRadius: 11,
          backgroundColor: '#FFFFFF',
          transform: checked
            ? `translateX(${isRTL ? -18 : 18}px)`
            : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.2)',
        }}
      />
    </button>
  );
}

export function PublicProfileBuilderScreen() {
  const { isRTL } = useLocale();
  const { t } = useTranslation('common');
  const router = useRouter();
  const pathname = usePathname();
  const toast = useToast();
  const upgradeModal = useUpgradeModal();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const dragPreviewRef = useRef<HTMLElement | null>(null);
  const previousPathnameRef = useRef(pathname);
  const [fallbackScrollHeight, setFallbackScrollHeight] = useState<
    number | null
  >(null);
  const [activeRoleShare, setActiveRoleShare] =
    useState<PublicProfileRenderOpenRole | null>(null);
  const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null);
  const [dragOverBlockId, setDragOverBlockId] = useState<string | null>(null);

  const {
    isPreview,
    setIsPreview,
    isHydrated,
    isLoading,
    loadError,
    isSaving,
    saveError,
    clearSaveError,
    retry,
    profileMode,
    routeMode,
    draftProfile,
    username,
    avatarUrl,
    publicPrivacy,
    publicUrl,
    blocks,
    toggleBlockEnabled,
    reorderBlocksByIndices,
    socials,
    contacts,
    theme: profileThemeId,
    accent,
    buttonStyle,
    buttonShape,
    completionPercent,
    hideBranding,
    setHideBranding,
    setHasSharedTabbio,
    projects,
    serviceOffers,
    teamMembers,
    isPubliclyVisible,
  } = usePublicProfileBuilderModel();

  // A company's website is a hero link, not a section; legacy pages still store
  // the block it used to ship with. Hidden from the list, left in storage.
  const editableBlocks = useMemo(
    () => getEditableProfileBlocks(blocks, routeMode),
    [blocks, routeMode],
  );
  const companyOpenRoles = useCompanyOpenRoles();

  const isCompanyMode = profileMode === 'company';
  // Company logos get the squared treatment the published page uses; people
  // stay round. Keeps the 54px header avatar in step with `ProfileSurface`.
  const avatarRadius = isCompanyMode ? 14 : 27;
  const profileRoutes = useMemo(
    () => getPublicProfileRoutes(routeMode),
    [routeMode],
  );
  const profileDisplayName = useMemo(() => {
    const fallback = username || 'Tabbio';
    if (isCompanyMode) {
      return draftProfile.name.trim() || fallback;
    }
    return `@${username}`;
  }, [draftProfile.name, isCompanyMode, username]);
  const profileHeadline = useMemo(() => {
    // Company mode has no editor for the long description any more, so reading
    // `bio` first let a stale stored value outrank the tagline the owner CAN
    // change - and left them no way to correct the header. The published
    // renderers already drop it (see packages/public-renderers/src/profile.tsx).
    if (isCompanyMode) {
      return (
        draftProfile.tagline.trim() ||
        t('publicProfile.company.defaultHeadline' as never, {
          name: draftProfile.name || username,
        })
      );
    }
    if (draftProfile.bio.trim()) {
      return draftProfile.bio.trim();
    }
    return t('publicProfile.defaultHeadline');
  }, [
    draftProfile.bio,
    draftProfile.name,
    draftProfile.tagline,
    isCompanyMode,
    t,
    username,
  ]);
  const publicBaseUrl = useMemo(() => {
    const clean = publicUrl.trim().replace(/\/$/, '');
    return clean || buildPublicProfileUrl(username, routeMode) || '';
  }, [publicUrl, routeMode, username]);
  const publicUrlLabel = useMemo(
    () => publicBaseUrl.replace(/^https?:\/\//, ''),
    [publicBaseUrl],
  );
  // Company header subtitle: the canonical public URL from the shared helper.
  // `username` is the company slug when routeMode === 'company'.
  const companyUrlLabel = useMemo(
    () =>
      (buildPublicProfileUrl(username, 'company') ?? '').replace(
        /^https?:\/\//,
        '',
      ) || publicUrlLabel,
    [publicUrlLabel, username],
  );
  const previewModelInput = useMemo(
    () => ({
      routeMode,
      draftProfile,
      username,
      avatarUrl,
      privacy: publicPrivacy,
      publicBaseUrl,
      publicUrlLabel,
      blocks,
      socials,
      contacts,
      projects,
      serviceOffers,
      teamMembers,
      companyRoles:
        companyOpenRoles.liveRoles as PublicProfilePreviewCompanyRole[],
      defaultHeadline: t('publicProfile.defaultHeadline'),
      companyDefaultHeadline: t(
        'publicProfile.company.defaultHeadline' as never,
        {
          name: draftProfile.name || username,
        },
      ),
      design: {
        theme: profileThemeId,
        accent,
        buttonStyle,
        buttonShape,
      },
      remoteLabels: {
        remote: t('publicProfile.previewSurface.remote'),
        hybrid: t('publicProfile.previewSurface.hybrid'),
        onSite: t('publicProfile.previewSurface.onSite'),
      },
      hideBranding,
    }),
    [
      accent,
      avatarUrl,
      blocks,
      buttonShape,
      buttonStyle,
      companyOpenRoles.liveRoles,
      contacts,
      draftProfile,
      hideBranding,
      profileThemeId,
      projects,
      publicPrivacy,
      publicBaseUrl,
      publicUrlLabel,
      routeMode,
      serviceOffers,
      socials,
      t,
      teamMembers,
      username,
    ],
  );
  const previewModel = useMemo(
    () => createPublicProfilePreviewModel(previewModelInput),
    [previewModelInput],
  );
  const routeSegment = routeMode === 'company' ? 'company' : 'profile';
  const getBlockListSubtitle = useCallback(
    (block: (typeof blocks)[number]) => {
      if (block.customSubtitle?.trim()) {
        return block.customSubtitle;
      }

      const meta = PUBLIC_PROFILE_BLOCK_META[block.type];

      if (block.type === 'website' && draftProfile.website.trim()) {
        return draftProfile.website.trim();
      }

      if (
        (block.type === 'visit' || block.type === 'map') &&
        draftProfile.location.trim()
      ) {
        return draftProfile.location.trim();
      }

      if (block.type === 'portfolio' && projects.length > 0) {
        return t('publicProfile.blockCounts.projects' as never, {
          count: projects.length,
        });
      }

      if (block.type === 'services' && serviceOffers.length > 0) {
        return t('publicProfile.blockCounts.services' as never, {
          count: serviceOffers.length,
        });
      }

      if (block.type === 'team') {
        const visibleTeamCount = teamMembers.filter(
          (member) => member.enabled,
        ).length;
        if (visibleTeamCount > 0) {
          return t('publicProfile.blockCounts.teamMembers' as never, {
            count: visibleTeamCount,
          });
        }
      }

      // Company mode renders the expanded CareersBlockInline card (with its
      // own "{open} open · {total} total" header) instead of this collapsed
      // row, so only the individual builder reaches this branch.
      if (block.type === 'openroles') {
        const rolesCount = block.roles?.length ?? 0;
        const applicantCount = (block.roles ?? []).reduce(
          (sum, role) => sum + (role.applications ?? 0),
          0,
        );

        if (rolesCount > 0 || applicantCount > 0) {
          return [
            t('publicProfile.openRoles.statsRoles' as never, {
              count: rolesCount,
            }),
            t('publicProfile.openRoles.statsApplicants' as never, {
              count: applicantCount,
            }),
          ].join(' · ');
        }
      }

      return t(meta.subtitleKey as never);
    },
    [
      draftProfile.location,
      draftProfile.website,
      projects.length,
      serviceOffers.length,
      t,
      teamMembers,
    ],
  );
  const orderedTopSocials = useMemo(
    () =>
      // Company mode shows the Phase 1 platform set (PRD §6.2); individual
      // keeps the historical top-five row.
      (routeMode === 'company'
        ? PUBLIC_PROFILE_SOCIAL_ORDER_COMPANY
        : PUBLIC_PROFILE_TOP_SOCIALS
      )
        .map((id) => socials.find((item) => item.id === id))
        .filter((item): item is NonNullable<typeof item> =>
          Boolean(item?.enabled),
        ),
    [routeMode, socials],
  );

  const orderedTopContacts = useMemo(
    () =>
      PUBLIC_PROFILE_CONTACT_ROW_ORDER.map((id) =>
        contacts.find((item) => item.id === id),
      ).filter((item): item is NonNullable<typeof item> =>
        Boolean(item?.enabled),
      ),
    [contacts],
  );

  const onOpen = useCallback(
    (path: PublicProfileRoutePath, markShared = false) => {
      if (markShared) setHasSharedTabbio(true);
      router.push(path as never);
    },
    [router, setHasSharedTabbio],
  );

  // Careers inline block (PRD §5.2/§6.6): job create/edit share the v2 flow.
  const openCreateJob = useCallback(() => {
    router.push('/(employer)/company/create-job' as never);
  }, [router]);

  const openEditJob = useCallback(
    (jobId: string) => {
      router.push(`/(employer)/company/create-job?jobId=${jobId}` as never);
    },
    [router],
  );

  const openExternal = useCallback(
    (url: string | null) => {
      if (!url) {
        toast.show({ title: t('publicProfile.shareSheet.unavailable') });
        return;
      }

      if (typeof window === 'undefined') return;
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        window.location.href = url;
      }
    },
    [t, toast],
  );

  const openPreviewHref = useCallback(
    (href: string) => {
      if (href.startsWith('/(')) {
        router.push(href as never);
        return;
      }
      openExternal(href);
    },
    [openExternal, router],
  );

  const resolveRoleHref = useCallback((role: PublicProfileRenderOpenRole) => {
    if (role.canonicalUrl) return role.canonicalUrl;
    if (role.applyUrl) return role.applyUrl;
    if (role.publicSlug) return buildCanonicalJobUrl(role.publicSlug);
    return buildAppJobUrl(role.id);
  }, []);

  const resolveRoleShareUrl = useCallback(
    (role: PublicProfileRenderOpenRole) => {
      if (role.canonicalUrl) return role.canonicalUrl;
      if (role.applyUrl) return role.applyUrl;
      if (role.publicSlug) return buildCanonicalJobUrl(role.publicSlug);
      return buildAppJobUrl(role.id);
    },
    [],
  );

  const getRolePathLabel = useCallback(
    (role: PublicProfileRenderOpenRole) => {
      const roleUrl = resolveRoleShareUrl(role);
      try {
        const parsed = new URL(roleUrl);
        return parsed.pathname;
      } catch {
        return roleUrl.replace(/^https?:\/\//, '');
      }
    },
    [resolveRoleShareUrl],
  );

  const handleRoleCopyLink = useCallback(
    async (role: PublicProfileRenderOpenRole) => {
      const roleUrl = resolveRoleShareUrl(role);
      try {
        if (
          typeof navigator === 'undefined' ||
          !navigator.clipboard?.writeText
        ) {
          throw new Error('Clipboard unavailable');
        }
        await navigator.clipboard.writeText(roleUrl);
        toast.show({ title: t('publicProfile.shareSheet.copied') });
      } catch {
        toast.show({ title: t('publicProfile.shareSheet.unavailable') });
      }
    },
    [resolveRoleShareUrl, t, toast],
  );

  const handleRoleShare = useCallback(
    async (role: PublicProfileRenderOpenRole) => {
      const roleUrl = resolveRoleShareUrl(role);
      const sharePayload = {
        title: role.title,
        text: role.title,
        url: roleUrl,
      };

      try {
        if (typeof navigator !== 'undefined' && navigator.share) {
          await navigator.share(sharePayload);
        } else {
          await handleRoleCopyLink(role);
        }
      } catch {
        toast.show({ title: t('publicProfile.shareSheet.unavailable') });
      }
    },
    [handleRoleCopyLink, resolveRoleShareUrl, t, toast],
  );

  const handleRoleMore = useCallback((role: PublicProfileRenderOpenRole) => {
    setActiveRoleShare(role);
  }, []);

  const clearBlockDragState = useCallback(() => {
    if (dragPreviewRef.current?.parentNode) {
      dragPreviewRef.current.parentNode.removeChild(dragPreviewRef.current);
    }
    dragPreviewRef.current = null;
    setDraggedBlockId(null);
    setDragOverBlockId(null);
  }, []);

  const handleBlockDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>, blockId: string) => {
      event.stopPropagation();
      const dragHandle = event.currentTarget as HTMLElement;
      const blockCard = dragHandle.closest(
        '[data-profile-block-card]',
      ) as HTMLElement | null;
      if (blockCard) {
        if (dragPreviewRef.current?.parentNode) {
          dragPreviewRef.current.parentNode.removeChild(dragPreviewRef.current);
        }

        const dragPreview = blockCard.cloneNode(true) as HTMLElement;
        dragPreview.style.position = 'fixed';
        dragPreview.style.top = '-9999px';
        dragPreview.style.left = '-9999px';
        dragPreview.style.margin = '0';
        dragPreview.style.pointerEvents = 'none';
        dragPreview.style.zIndex = '99999';
        dragPreview.style.width = `${blockCard.getBoundingClientRect().width}px`;
        dragPreview.style.opacity = '1';
        dragPreview.style.borderRadius = '16px';
        dragPreview.style.overflow = 'visible';
        dragPreview.style.boxSizing = 'border-box';
        dragPreview.style.transform = 'none';
        document.body.appendChild(dragPreview);
        dragPreviewRef.current = dragPreview;

        event.dataTransfer.setDragImage(dragPreview, 40, 24);
      }
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', blockId);
      setDraggedBlockId(blockId);
      setDragOverBlockId(blockId);
    },
    [],
  );

  const handleBlockDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>, blockId: string) => {
      if (!draggedBlockId || draggedBlockId === blockId) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (dragOverBlockId !== blockId) {
        setDragOverBlockId(blockId);
      }
    },
    [dragOverBlockId, draggedBlockId],
  );

  const handleBlockDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>, blockId: string) => {
      event.preventDefault();
      const sourceId =
        draggedBlockId || event.dataTransfer.getData('text/plain');
      if (!sourceId || sourceId === blockId) {
        clearBlockDragState();
        return;
      }

      const fromIndex = blocks.findIndex((block) => block.id === sourceId);
      const toIndex = blocks.findIndex((block) => block.id === blockId);
      if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
        reorderBlocksByIndices(fromIndex, toIndex);
      }

      clearBlockDragState();
    },
    [blocks, clearBlockDragState, draggedBlockId, reorderBlocksByIndices],
  );

  const shareToX = useCallback(
    (role: PublicProfileRenderOpenRole) => {
      const roleUrl = resolveRoleShareUrl(role);
      openExternal(
        `https://x.com/intent/tweet?url=${encodeURIComponent(roleUrl)}&text=${encodeURIComponent(role.title)}`,
      );
    },
    [openExternal, resolveRoleShareUrl],
  );

  const shareToWhatsApp = useCallback(
    (role: PublicProfileRenderOpenRole) => {
      const roleUrl = resolveRoleShareUrl(role);
      openExternal(
        `https://wa.me/?text=${encodeURIComponent(`${role.title} · ${roleUrl}`)}`,
      );
    },
    [openExternal, resolveRoleShareUrl],
  );

  const shareToLinkedIn = useCallback(
    (role: PublicProfileRenderOpenRole) => {
      const roleUrl = resolveRoleShareUrl(role);
      openExternal(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(roleUrl)}`,
      );
    },
    [openExternal, resolveRoleShareUrl],
  );

  const roleShareActions = useMemo(() => {
    if (!activeRoleShare) return [];

    return [
      {
        id: 'copy',
        label: t('publicProfile.shareSheet.copyLink'),
        icon: (
          <div
            style={{
              width: 58,
              height: 58,
              borderRadius: 29,
              background: 'rgba(0,0,0,0.04)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <MaterialSymbol
              name="content_copy"
              size={18}
              style={{ color: 'var(--text-secondary)' }}
            />
          </div>
        ),
        onClick: () => {
          void handleRoleCopyLink(activeRoleShare);
        },
      },
      {
        id: 'x',
        label: t('publicProfile.shareSheet.platforms.x'),
        icon: (
          <div
            style={{
              width: 58,
              height: 58,
              borderRadius: 29,
              background: '#000000',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <ProfileBrandIcon id="x" size={18} color="#ffffff" />
          </div>
        ),
        onClick: () => shareToX(activeRoleShare),
      },
      {
        id: 'whatsapp',
        label: t('publicProfile.shareSheet.platforms.whatsapp'),
        icon: (
          <div
            style={{
              width: 58,
              height: 58,
              borderRadius: 29,
              background: '#25D366',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <ProfileBrandIcon id="whatsapp" size={18} color="#ffffff" />
          </div>
        ),
        onClick: () => shareToWhatsApp(activeRoleShare),
      },
      {
        id: 'linkedin',
        label: t('publicProfile.shareSheet.platforms.linkedin'),
        icon: (
          <div
            style={{
              width: 58,
              height: 58,
              borderRadius: 29,
              background: '#0A66C2',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <ProfileBrandIcon id="linkedin" size={18} color="#ffffff" />
          </div>
        ),
        onClick: () => shareToLinkedIn(activeRoleShare),
      },
    ];
  }, [
    activeRoleShare,
    handleRoleCopyLink,
    shareToLinkedIn,
    shareToWhatsApp,
    shareToX,
    t,
  ]);

  const recomputeScrollFallback = useCallback(() => {
    if (typeof window === 'undefined') return;

    const appHeaderHeight = 48;
    const minimumExpectedHeight = Math.max(
      window.innerHeight - appHeaderHeight,
      320,
    );
    const measuredHeight =
      scrollContainerRef.current?.getBoundingClientRect().height ??
      minimumExpectedHeight;
    const collapsed = measuredHeight < minimumExpectedHeight * 0.72;

    setFallbackScrollHeight(collapsed ? minimumExpectedHeight : null);
  }, []);

  useEffect(() => {
    const previousPath = previousPathnameRef.current;
    const currentPath = pathname ?? '';
    const wasProfileChild = new RegExp(`\\/${routeSegment}\\/.+`, 'i').test(
      previousPath ?? '',
    );
    const isProfileIndex = new RegExp(`\\/${routeSegment}$`, 'i').test(
      currentPath,
    );

    if (wasProfileChild && isProfileIndex) {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    }

    previousPathnameRef.current = currentPath;
  }, [pathname, routeSegment]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let frame = window.requestAnimationFrame(recomputeScrollFallback);
    const onResize = () => {
      window.requestAnimationFrame(recomputeScrollFallback);
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, [recomputeScrollFallback, pathname, isPreview]);

  if (!isHydrated && isLoading && !loadError) {
    return <DocumentDetailSkeleton />;
  }

  if (!isHydrated && loadError) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          height: '100%',
          backgroundColor: 'var(--bg)',
          padding: 24,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 520,
            borderRadius: 20,
            border: `1px solid ${'var(--input-border)'}`,
            backgroundColor: 'rgba(0,0,0,0.04)',
            padding: 24,
            display: 'grid',
            gap: 12,
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 500,
              color: 'var(--text-primary)',
            }}
          >
            {t('publicProfile.status.errorTitle')}
          </div>
          <div
            style={{
              fontSize: 14,
              lineHeight: '20px',
              color: 'var(--text-tertiary)',
            }}
          >
            {loadError ?? t('publicProfile.status.errorBody')}
          </div>
          <button
            onClick={() => {
              void retry();
            }}
            style={{
              width: 'fit-content',
              minHeight: 38,
              borderRadius: 999,
              border: `1px solid ${'var(--input-border)'}`,
              backgroundColor: 'var(--bg)',
              color: 'var(--text-primary)',
              paddingInline: 14,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {t('actions.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (isPreview) {
    return (
      <>
        <PublicProfilePreview
          model={previewModel}
          modelInput={previewModelInput}
          isPubliclyVisible={isPubliclyVisible}
          onEdit={() => setIsPreview(false)}
          onShare={() => onOpen(profileRoutes.share, true)}
          onRoleMore={handleRoleMore}
          onOpenSettings={() => onOpen(profileRoutes.settings, true)}
          onOpenHref={openPreviewHref}
          onOpenMenu={() => setIsPreview(false)}
          onUpgrade={() =>
            upgradeModal.open({
              placement: 'branding',
              entryPoint: 'public_profile.web_preview',
              planSet: routeMode === 'company' ? 'employer' : 'individual',
              recommendedTier: 'plus',
              featureKey:
                routeMode === 'company'
                  ? 'employer.hiring_pages'
                  : 'individual.custom_branding',
              reason: 'wrong_tier',
              returnTo: routeMode === 'company' ? '/company' : '/profile',
            })
          }
          onOpenTalentPool={() => onOpen(profileRoutes.openRoles)}
        />
        {isCompanyMode ? (
          <button
            onClick={() => setIsPreview(false)}
            style={{
              position: 'absolute',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 60,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 44,
              paddingInline: 20,
              borderRadius: 9999,
              border: 'none',
              backgroundColor: colors.primary,
              color: lightTheme.buttonPrimaryText,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              boxShadow: 'var(--gem-dialog-shadow)',
            }}
          >
            <MaterialSymbol
              name="edit"
              size={18}
              style={{ color: lightTheme.buttonPrimaryText }}
            />
            {t('publicProfile.company.backToEditor')}
          </button>
        ) : null}
        <WebShareDialog
          visible={Boolean(activeRoleShare)}
          title={t('actions.share')}
          onClose={() => setActiveRoleShare(null)}
          preview={
            activeRoleShare ? (
              <div
                style={{
                  borderRadius: 18,
                  minHeight: 168,
                  backgroundColor: '#07070A',
                  display: 'grid',
                  alignContent: 'center',
                  justifyItems: 'center',
                  gap: 10,
                  padding: '18px',
                  textAlign: 'center',
                }}
              >
                <MaterialSymbol
                  name="content_copy"
                  size={22}
                  style={{ color: '#ffffff' }}
                />
                <div
                  style={{
                    color: '#ffffff',
                    fontSize: 32,
                    lineHeight: '40px',
                    fontWeight: 500,
                  }}
                >
                  {activeRoleShare.title}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    color: 'rgba(255,255,255,0.72)',
                  }}
                >
                  <ProfileBrandIcon
                    id="tabbio"
                    size={15}
                    color="rgba(255,255,255,0.72)"
                  />
                  <span
                    style={{
                      fontSize: 16,
                      lineHeight: '22px',
                      fontWeight: 500,
                    }}
                  >
                    {getRolePathLabel(activeRoleShare)}
                  </span>
                </div>
              </div>
            ) : null
          }
          actions={roleShareActions}
          footer={
            activeRoleShare ? (
              <div
                style={{
                  borderRadius: 20,
                  background: 'rgba(0,0,0,0.04)',
                  padding: 16,
                  display: 'grid',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 20,
                    lineHeight: '26px',
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                  }}
                >
                  {t('publicProfile.previewSurface.applyNow')}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    lineHeight: '20px',
                    color: 'var(--text-tertiary)',
                  }}
                >
                  {t('publicProfile.previewSurface.talentPoolCta')}
                </div>
                <div style={{ marginTop: 4, display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => {
                      setActiveRoleShare(null);
                      openPreviewHref(resolveRoleHref(activeRoleShare));
                    }}
                    style={{
                      flex: 1,
                      minHeight: 48,
                      borderRadius: 999,
                      border: 'none',
                      backgroundColor: 'var(--text-primary)',
                      color: 'var(--bg)',
                      fontSize: 15,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    {t('publicProfile.previewSurface.applyNow')}
                  </button>
                  <button
                    onClick={() => setActiveRoleShare(null)}
                    style={{
                      flex: 1,
                      minHeight: 48,
                      borderRadius: 999,
                      border: 'none',
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'var(--text-primary)',
                      fontSize: 15,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    {t('actions.close')}
                  </button>
                </div>
              </div>
            ) : null
          }
        />
      </>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        position: 'relative',
        backgroundColor: 'var(--bg)',
      }}
    >
      <div
        ref={scrollContainerRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          height: fallbackScrollHeight ?? undefined,
          maxHeight: fallbackScrollHeight ?? undefined,
          padding: '18px 0 140px',
        }}
      >
        <div
          style={{
            maxWidth: 760,
            width: '100%',
            margin: '0 auto',
            paddingInline: 20,
          }}
        >
          {/* Save status: floating toast, doesn't jiggle layout */}
          {isHydrated && (isSaving || saveError) ? (
            <div
              role="status"
              aria-live="polite"
              style={{
                position: 'fixed',
                top: 20,
                insetInlineEnd: 20,
                zIndex: 50,
                maxWidth: 320,
                borderRadius: 9999,
                backgroundColor: 'var(--menu-bg)',
                boxShadow: 'var(--gem-input-shadow)',
                border: '1px solid var(--input-border)',
                padding: '10px 16px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                fontFamily: 'inherit',
                animation: 'tabbio-toast-in 180ms cubic-bezier(0.22,1,0.36,1)',
              }}
            >
              {isSaving ? (
                <span
                  aria-hidden
                  style={{
                    width: 12,
                    height: 12,
                    border: '2px solid rgba(0,0,0,0.15)',
                    borderTopColor: 'var(--text-primary)',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <span
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#d93025',
                    flexShrink: 0,
                  }}
                />
              )}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {isSaving
                  ? t('publicProfile.status.savingTitle')
                  : (saveError ?? t('publicProfile.status.saveErrorTitle'))}
              </span>
              {saveError ? (
                <button
                  type="button"
                  onClick={clearSaveError}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    fontSize: 13,
                    fontWeight: 500,
                    padding: 0,
                    marginInlineStart: 4,
                    fontFamily: 'inherit',
                  }}
                >
                  {t('actions.dismiss')}
                </button>
              ) : null}
              <style>{`
              @keyframes tabbio-toast-in {
                from { opacity: 0; transform: translateY(-6px); }
                to { opacity: 1; transform: translateY(0); }
              }
              @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
            </div>
          ) : null}
          <WebPageHeader
            title={
              isCompanyMode
                ? t('nav.company', { defaultValue: 'Company Page' })
                : t('nav.profile', { defaultValue: 'Profile' })
            }
            subtitle={
              isCompanyMode ? companyUrlLabel : `${username}.tabbio.com`
            }
            onBack={() => {
              if (isCompanyMode) {
                router.push('/(employer)/chat' as never);
                return;
              }
              if (typeof window !== 'undefined' && window.history.length > 1) {
                router.back();
                return;
              }
              router.push('/(app)/chat' as never);
            }}
          >
            <BubbleButton
              size={42}
              bg={'rgba(0,0,0,0.04)'}
              onClick={() => onOpen(profileRoutes.share, true)}
              ariaLabel={t('publicProfile.shareSheet.title')}
            >
              <MaterialSymbol
                name="ios_share"
                size={18}
                style={{ color: 'var(--text-primary)' }}
              />
            </BubbleButton>
          </WebPageHeader>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 10,
            }}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={profileDisplayName}
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: avatarRadius,
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: avatarRadius,
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  color: 'var(--text-secondary)',
                  fontSize: 20,
                  fontWeight: 700,
                }}
              >
                {(profileDisplayName || 'T')
                  .replace('@', '')
                  .charAt(0)
                  .toUpperCase()}
              </div>
            )}

            <div
              role="button"
              tabIndex={0}
              onClick={() => onOpen(profileRoutes.editProfile)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpen(profileRoutes.editProfile);
                }
              }}
              style={{
                padding: 0,
                margin: 0,
                textAlign: 'start',
                flex: 1,
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    color: 'var(--text-primary)',
                    fontSize: 22,
                    fontWeight: 500,
                    lineHeight: '28px',
                  }}
                >
                  {profileDisplayName}
                </span>
                <MaterialSymbol
                  name="edit"
                  size={15}
                  style={{ color: 'var(--text-tertiary)' }}
                />
                {/* Company mode moves Settings into the dock (one entry point,
                    next to Analytics); individual keeps the header control. */}
                {!isCompanyMode ? (
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpen(profileRoutes.settings);
                    }}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      display: 'grid',
                      placeItems: 'center',
                      cursor: 'pointer',
                    }}
                    aria-label={t('publicProfile.settingsSheet.title')}
                  >
                    <MaterialSymbol
                      name="tune"
                      size={15}
                      style={{ color: 'var(--text-tertiary)' }}
                    />
                  </button>
                ) : null}
              </div>
              <div
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: 14,
                  lineHeight: '19px',
                  fontWeight: 500,
                }}
              >
                {profileHeadline}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 2,
            }}
          >
            {/* The website sits with the socials, matching the public hero:
                it is one outbound link like the rest, and it is edited from the
                same sheet. It used to have no chip here at all, which is why it
                only ever showed up as a block. */}
            {draftProfile.website.trim() ? (
              <BubbleButton
                bg={'rgba(0,0,0,0.04)'}
                onClick={() => onOpen(profileRoutes.socials)}
                ariaLabel={t('publicProfile.socials.website.label')}
              >
                <MaterialSymbol
                  name="language"
                  size={19}
                  style={{ color: 'var(--text-secondary)' }}
                />
              </BubbleButton>
            ) : null}
            {orderedTopSocials.map((social) => (
              <BubbleButton
                key={social.id}
                bg={'rgba(0,0,0,0.04)'}
                onClick={() => onOpen(profileRoutes.socials)}
                ariaLabel={social.id}
              >
                <ProfileBrandIcon
                  id={mapSocialIcon(social.id)}
                  size={17}
                  color={'var(--text-secondary)'}
                />
              </BubbleButton>
            ))}
            <BubbleButton
              bg={'rgba(0,0,0,0.04)'}
              onClick={() => onOpen(profileRoutes.socials)}
              ariaLabel={t('publicProfile.socialSheet.title')}
            >
              <MaterialSymbol
                name="add"
                size={20}
                style={{ color: 'var(--text-secondary)' }}
              />
            </BubbleButton>
          </div>

          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}
          >
            {orderedTopContacts.map((contact) => (
              <BrandBubble
                key={contact.id}
                id={mapContactIcon(contact.id)}
                onClick={() => onOpen(profileRoutes.contacts)}
              />
            ))}
            <BubbleButton
              bg={'rgba(0,0,0,0.04)'}
              onClick={() => onOpen(profileRoutes.contacts)}
              ariaLabel={t('publicProfile.contactSheet.title')}
            >
              <MaterialSymbol
                name="add"
                size={20}
                style={{ color: 'var(--text-secondary)' }}
              />
            </BubbleButton>
          </div>

          {/* Company mode hides this row: the dock's Add button owns block
            adding per the approved Phase 1 layout. */}
          {!isCompanyMode ? (
            <div
              style={{
                marginTop: 14,
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <button
                onClick={() => onOpen(profileRoutes.add)}
                style={{
                  minHeight: 44,
                  borderRadius: 999,
                  border: 'none',
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  paddingInline: 16,
                  cursor: 'pointer',
                  transition: 'background-color 140ms',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    'rgba(0,0,0,0.06)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    'rgba(0,0,0,0.04)';
                }}
              >
                <MaterialSymbol
                  name="add"
                  size={18}
                  style={{ color: 'var(--text-primary)' }}
                />
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                  }}
                >
                  {t('publicProfile.addCollection')}
                </span>
              </button>
              <span
                style={{
                  color: 'var(--text-tertiary)',
                  fontSize: 15,
                  fontWeight: 500,
                }}
              >
                {editableBlocks.filter((block) => block.enabled).length}{' '}
                {t('publicProfile.activeBlocks')}
              </span>
            </div>
          ) : (
            <div style={{ height: 14 }} />
          )}

          <div style={{ display: 'grid', gap: 8 }}>
            {editableBlocks.map((block) => {
              // Company mode: Careers renders expanded inline (PRD §5.2/§6.6)
              // — jobs, publish toggles, and the future-roles row live in the
              // list itself. Header press still opens the Open Roles sheet.
              if (
                block.type === 'openroles' &&
                companyOpenRoles.isCompanyMode
              ) {
                return (
                  <CareersBlockInline
                    key={block.id}
                    block={block}
                    jobs={companyOpenRoles.jobs}
                    isLoading={companyOpenRoles.isLoading}
                    isRTL={isRTL}
                    isDragging={draggedBlockId === block.id}
                    isDragOver={
                      dragOverBlockId === block.id &&
                      draggedBlockId !== block.id
                    }
                    onOpenEditor={() => onOpen(profileRoutes.openRoles)}
                    onToggleBlock={() => toggleBlockEnabled(block.id)}
                    onAddJob={openCreateJob}
                    onEditJob={openEditJob}
                    onDragStart={(event) =>
                      handleBlockDragStart(event, block.id)
                    }
                    onDragEnd={clearBlockDragState}
                    onDragOver={(event) => handleBlockDragOver(event, block.id)}
                    onDrop={(event) => handleBlockDrop(event, block.id)}
                    onDragEnter={() => {
                      if (draggedBlockId && draggedBlockId !== block.id) {
                        setDragOverBlockId(block.id);
                      }
                    }}
                  />
                );
              }
              const meta = PUBLIC_PROFILE_BLOCK_META[block.type];
              const iconName =
                MATERIAL_ICON_BY_BLOCK_TYPE[block.type] ?? 'apps';
              return (
                <BlockRow
                  key={block.id}
                  blockId={block.id}
                  iconName={iconName}
                  title={block.customTitle || t(meta.titleKey as never)}
                  subtitle={getBlockListSubtitle(block)}
                  enabled={block.enabled}
                  isRTL={isRTL}
                  isDragging={draggedBlockId === block.id}
                  isDragOver={
                    dragOverBlockId === block.id && draggedBlockId !== block.id
                  }
                  onOpenEditor={() =>
                    block.type === 'openroles'
                      ? onOpen(profileRoutes.openRoles)
                      : block.type === 'team'
                        ? onOpen(profileRoutes.team)
                        : onOpen(profileRoutes.block(block.id))
                  }
                  onToggle={() => toggleBlockEnabled(block.id)}
                  onDragStart={(event) => handleBlockDragStart(event, block.id)}
                  onDragEnd={clearBlockDragState}
                  onDragOver={(event) => handleBlockDragOver(event, block.id)}
                  onDrop={(event) => handleBlockDrop(event, block.id)}
                  onDragEnter={() => {
                    if (draggedBlockId && draggedBlockId !== block.id) {
                      setDragOverBlockId(block.id);
                    }
                  }}
                />
              );
            })}
            {isCompanyMode ? (
              <div
                style={{
                  minHeight: 120,
                  borderRadius: 20,
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  padding: '16px 18px',
                  display: 'grid',
                  gap: 14,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {t('publicProfile.company.footerTitle')}
                  </span>
                  <Toggle
                    checked={!hideBranding}
                    onToggle={() => setHideBranding(!hideBranding)}
                    isRTL={isRTL}
                  />
                </div>
                <div style={{ opacity: 0.9 }}>
                  <img
                    src={LOGO_MAIN}
                    alt="Tabbio"
                    style={{
                      width: 124,
                      maxWidth: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      // Neutral mark, not the purple one: this is a settings
                      // preview, so it should sit at the same weight as the
                      // row title rather than compete with it.
                      filter: 'var(--brand-wordmark-filter)',
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isCompanyMode ? (
        <>
          {/* Same bottom fade the /cv screen paints behind its dock
              (app/(app)/cv/index.web.tsx). */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 128,
              zIndex: 40,
              pointerEvents: 'none',
              background:
                'linear-gradient(to bottom, transparent 0%, var(--bg) 76%)',
            }}
          />
          <CompanyPageBottomBar
            placement="content"
            completionPercent={completionPercent}
            onSetupPress={() => onOpen(profileRoutes.checklist)}
            onAddPress={() => onOpen(profileRoutes.add)}
            onPreviewPress={() => setIsPreview(true)}
            onAnalyticsPress={() => onOpen(profileRoutes.analytics)}
            onSettingsPress={() => onOpen(profileRoutes.settings)}
          />
        </>
      ) : (
        <ProfileBottomBar
          completionPercent={completionPercent}
          onSetupPress={() => onOpen(profileRoutes.checklist)}
          onAddPress={() => onOpen(profileRoutes.add)}
          onPreviewPress={() => setIsPreview(true)}
          onStylePress={() => onOpen(profileRoutes.design)}
        />
      )}
    </div>
  );
}
